import { Button } from '@/components/button';
import styles from './photo-wrapper.module.css';
import { DeleteImageButton } from './delete-image-button';
import { useTranslation } from '@/contexts/i18n-context';
import { Photo, PhotoFormValues, photoSchema } from './photo-upload';
import clsx from 'clsx';
import { BodyLocationType } from '@/api/api.schemas';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { FormContext } from '@/components/form';
import { useCallback, useState } from 'react';
import { BodyLocationSelector } from '@/components/form-controls/body-location-selector';
import { PhotoDetailsModal } from './photo-details-modal';
import { useUpdateEncountersImages } from '@/api/update-image';
import { useEncounter } from '@/contexts/encounter-context';
import { Loader } from '@/components/loaders/loader';
import { Img } from '@/components/img';

const translationBase = 'visit.photoUpload';

interface PhotoWrapperProps {
  photo: Photo;
  alt: string;
  onDelete: (id: number) => void;
  handleUpdate: (id: number, location: BodyLocationType, description?: string) => void;
}

export function PhotoWrapper({ photo, onDelete, alt, handleUpdate }: PhotoWrapperProps) {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const { encounter } = useEncounter();
  const { mutate: updateImage } = useUpdateEncountersImages();

  const form = useForm<PhotoFormValues>({
    resolver: zodResolver(photoSchema),
    mode: 'onBlur',
    defaultValues: {
      bodyLocation: photo.bodyLocation ?? undefined,
      description: photo.description,
    },
  });

  const handleSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    form.handleSubmit(onSubmit)();
  };

  const onSubmit = useCallback(
    (data: PhotoFormValues) => {
      if (!photo.id) {
        return;
      }

      updateImage({
        encounterId: encounter.id,
        imageId: photo.id,
        data: { bodyLocation: data.bodyLocation, description: data.description },
      });
      handleUpdate(photo.id, data.bodyLocation, data.description);
      setIsOpen(false);
    },
    [photo, handleUpdate, encounter.id, updateImage],
  );

  return (
    <FormContext.Provider value={form}>
      <div className={styles.photoWrapper}>
        <div className={styles.imageWrapper}>
          {photo.id && (
            <DeleteImageButton imageId={photo.id} onDelete={() => onDelete(photo.id!)} />
          )}
          <Img src={photo.imageSrc} className={styles.image} alt={alt} />
        </div>
        {photo.id ? (
          <div className={clsx(styles.photoDetails)}>
            <BodyLocationSelector
              id={photo.id?.toString() || photo.imageSrc}
              name="bodyLocation"
              placeholder={t(`${translationBase}.bodyLocation.placeholder`)}
              className={styles.bodyLocationContainer}
              selectorClassName={clsx('body-3', styles.bodyLocationSelector)}
              onChange={({ value }) => {
                if (value !== photo.bodyLocation) {
                  form.handleSubmit(onSubmit)();
                }
              }}
            />
            {photo.description && (
              <p className={clsx(styles.description, 'body-4')}>{photo.description}</p>
            )}
            <Button size="S" leftIcon="edit" variant="link" onClick={() => setIsOpen(true)}>
              {t(`${translationBase}.${photo.description ? 'editDescription' : 'addDescription'}`)}
            </Button>
          </div>
        ) : (
          <div className={styles.loaderContainer}>
            <Loader type="small" />
          </div>
        )}
      </div>
      <PhotoDetailsModal
        photo={photo}
        isOpen={isOpen}
        alt={alt}
        onClose={() => setIsOpen(false)}
        handleSubmit={handleSubmit}
      />
    </FormContext.Provider>
  );
}
